import { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useSectionDispatch } from "../../context/SectionContextProvider"
import { useGetFormByProvince } from "../../hooks/useGetFormByProvince"
import { useInitialFormStateByProvince } from "../../hooks/useInitialFormState"
import { useScrollToTop } from "../../hooks/useScrollToTop"
import { SideNav } from "../SideNav"
import { SubmissionSplashPage } from "../SubmissionSplashPage"
import { Icon } from "../Icon"
import { useSubmissionStatus } from "./useSubmissionStatus"

export function MainPage() {
  const { province, step } = useParams<{ province: string; step: string }>()
  const { data, isLoading } = useGetFormByProvince(province ?? "ontario")
  const { initialFormState } = useInitialFormStateByProvince(
    province ?? "ontario",
  )
  const { isError, isSuccess, error } = useSubmissionStatus()
  const methods = useForm({
    defaultValues: initialFormState,
    shouldUnregister: false,
  })
  const dispatch = useSectionDispatch()
  const navigate = useNavigate()
  useScrollToTop()
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()

  const isSubmitting = methods.formState.isSubmitting

  useEffect(() => {
    if (data && step) {
      const reviewStep = data?.length + 1
      const stepsArray = [reviewStep]
      data?.forEach((_value, index) => {
        stepsArray.push(index + 1)
      })
      if (!stepsArray?.includes(Number(step))) {
        throw new Error(t("client.404.message"), {
          cause: { name: "404", message: "Page Not Found" },
        })
      }
    }
  }, [data, step, navigate, t])

  // this effect watches for changes to persist to session storage
  useEffect(() => {
    const subscription = methods.watch((value) => {
      sessionStorage.setItem(
        `form-state-${province}`,
        JSON.stringify(value, null, 2),
      )
    })
    return () => subscription.unsubscribe()
  }, [methods, methods.watch, province])

  useEffect(() => {
    data && dispatch({ type: "SET_SECTIONS", sections: data })
  }, [data, dispatch, resolvedLanguage])

  const goBackToReview = () => {
    navigate(`../steps/${step}`)
  }

  if (isLoading)
    return (
      <p className="flex flex-1 justify-center items-center h-[100vh]">
        Loading...
      </p>
    )

  if (isSubmitting || isSuccess || isError || error) {
    return (
      <SubmissionSplashPage
        isSubmitting={isSubmitting}
        isError={isError}
        isSuccess={isSuccess}
        onRetry={goBackToReview}
        error={error && decodeURI(error)}
      />
    )
  }

  //TODO: fallback if data fails to load
  if (!data) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full z-10 md:flex-row">
        <SideNav />
        <main className="flex flex-col w-full h-full pb-20 px-10 md:px-0">
          {province === "ontario" && (
            <button
              className="flex items-end self-end pr-28 pt-10"
              onClick={() => navigate(`../upload`)}
            >
              <div className="flex items-center text-[color:var(--alias-color-text-link-default)] [font-family:var(--alias-font-subtitle-lg-font-weight)]">
                <Icon size="sm" variant="Upload" className="mr-1" />
                {t("client.buttons.upload")}
              </div>
            </button>
          )}
          <div className="my-6 md:mr-20 md:mt-32 mt-10">
            <Outlet />
          </div>
        </main>
      </div>
    </FormProvider>
  )
}
